'use client';

import { useContext, useEffect, useMemo } from 'react';

import {
  CategoryDetail,
  Donation,
  Project,
  ProjectBeneficiaryTypeEnum,
  ProjectSettingsModeGroupEnum,
  Reward,
  Subproject,
  User,
} from '@/app/api';

import { CookiebotContext } from '@/app/components/CookiebotScript/CookiebotScript';
import { DonationFormBaseValues } from '@/app/components/DonationForm/types';
import { DonationFormType } from '@/app/enums';
import useAnalytics from '@/app/hooks/useAnalytics';
import { useCurrentUser } from '@/app/hooks/useCurrentUser';
import { useDonationAmountOptions } from '@/app/hooks/useDonationAmountOptions ';
import { useInactiveTabTitleFlash } from '@/app/hooks/useInactiveTabTitleFlash';
import { getCountryConfig } from '@/app/utils/countryConfig';
import { useTranslation } from 'react-i18next';

import DonationForm from '@/app/components/DonationForm/DonationForm';

const countryConfig = getCountryConfig();

const isPrivacyConsentCheckboxSupported = countryConfig.donationFormCommon.isPrivacyConsentCheckboxSupported;

interface DonationDetailProps {
  project: Project;
  subproject?: Subproject;
  categories: CategoryDetail[];
  initialValues?: Partial<DonationFormBaseValues>;
  currentUser?: User;
  projectBeneficiaryType: ProjectBeneficiaryTypeEnum | null;
  unassignedGoodhabits?: Donation[] | null;
  rewards?: Reward[];
}

const DonationDetail = ({
  project,
  subproject,
  categories,
  initialValues,
  currentUser,
  projectBeneficiaryType,
  unassignedGoodhabits,
  rewards,
}: DonationDetailProps) => {
  const {
    donation: { pushDonationFormPageViewEvent },
  } = useAnalytics();
  const { t } = useTranslation();

  const { isLoadingUser } = useCurrentUser();

  const amountOptions = useDonationAmountOptions(project.settings.donationAmounts);

  useInactiveTabTitleFlash({
    originalTitle: `${project.content.currentName} | ${t('appName')}`,
    inactiveTitle: t('pages.donationForm.finishTheDonation'),
  });

  const { areStatisticsCookiesAllowed } = useContext(CookiebotContext);

  const shouldDisplayPrivacyConsentCheckbox =
    projectBeneficiaryType === ProjectBeneficiaryTypeEnum.Organization &&
    project.settings.modeGroup === ProjectSettingsModeGroupEnum.Dobro &&
    isPrivacyConsentCheckboxSupported;

  const formType = useMemo(() => {
    if (project.settings.modeGroup === ProjectSettingsModeGroupEnum.Plus) {
      return DonationFormType.COMMERCIAL_PROJECT;
    }

    if (project.settings.isGoodHabit) {
      if (currentUser) {
        return DonationFormType.GOOD_HABIT_AUTHENTICATED_USER;
      }

      return DonationFormType.GOOD_HABIT_UNAUTHENTICATED_USER;
    }

    if (!project.settings.isLongTerm) {
      if (currentUser) {
        return DonationFormType.SINGLE_AUTHENTICATED_USER;
      }

      return DonationFormType.SINGLE_UNAUTHENTICATED_USER;
    }

    if (project.settings.isLongTerm) {
      if (currentUser) {
        return DonationFormType.LONG_TERM_AUTHENTICATED_USER;
      }

      return DonationFormType.LONG_TERM_UNAUTHENTICATED_USER;
    }

    return DonationFormType.SINGLE_UNAUTHENTICATED_USER;
  }, [project, currentUser]);

  useEffect(() => {
    if (!isLoadingUser && areStatisticsCookiesAllowed) {
      pushDonationFormPageViewEvent(project);
    }
  }, [areStatisticsCookiesAllowed]);

  switch (formType) {
    case DonationFormType.LONG_TERM_AUTHENTICATED_USER:
      return (
        <div className="w-full min-h-[100vh] rounded md:pb-[40px]">
          <DonationForm
            amountOptions={amountOptions}
            unassignedGoodhabits={unassignedGoodhabits}
            project={project}
            subproject={subproject}
            formType={formType}
            currentUser={currentUser ?? undefined}
            initialValues={initialValues}
            shouldDisplayPrivacyConsentCheckbox={shouldDisplayPrivacyConsentCheckbox}
          />
        </div>
      );

    case DonationFormType.LONG_TERM_UNAUTHENTICATED_USER:
      return (
        <div className="w-full min-h-[100vh] rounded md:pb-[40px]">
          <DonationForm
            amountOptions={amountOptions}
            unassignedGoodhabits={unassignedGoodhabits}
            project={project}
            subproject={subproject}
            formType={formType}
            initialValues={initialValues}
            shouldDisplayPrivacyConsentCheckbox={shouldDisplayPrivacyConsentCheckbox}
          />
        </div>
      );

    case DonationFormType.GOOD_HABIT_AUTHENTICATED_USER:
      return (
        <div className="w-full min-h-[100vh] rounded md:pb-[40px]">
          <DonationForm
            amountOptions={amountOptions}
            currentUser={currentUser ?? undefined}
            project={project}
            subproject={subproject}
            formType={formType}
            initialValues={initialValues}
            categories={categories}
            shouldDisplayPrivacyConsentCheckbox={shouldDisplayPrivacyConsentCheckbox}
          />
        </div>
      );

    case DonationFormType.GOOD_HABIT_UNAUTHENTICATED_USER:
      return (
        <div className="w-full min-h-[100vh] rounded md:pb-[40px]">
          <DonationForm
            amountOptions={amountOptions}
            project={project}
            subproject={subproject}
            formType={formType}
            initialValues={initialValues}
            categories={categories}
            shouldDisplayPrivacyConsentCheckbox={shouldDisplayPrivacyConsentCheckbox}
          />
        </div>
      );

    case DonationFormType.COMMERCIAL_PROJECT:
      return (
        <div className="w-full min-h-[100vh] rounded md:pb-[40px]">
          <DonationForm
            rewards={rewards}
            project={project}
            subproject={subproject}
            formType={formType}
            initialValues={initialValues}
            currentUser={currentUser ?? undefined}
            shouldDisplayPrivacyConsentCheckbox={shouldDisplayPrivacyConsentCheckbox}
          />
        </div>
      );

    case DonationFormType.SINGLE_AUTHENTICATED_USER:
      return (
        <div className="w-full min-h-[100vh] rounded md:pb-[40px]">
          <DonationForm
            amountOptions={amountOptions}
            unassignedGoodhabits={unassignedGoodhabits}
            project={project}
            subproject={subproject}
            formType={formType}
            initialValues={initialValues}
            currentUser={currentUser ?? undefined}
            shouldDisplayPrivacyConsentCheckbox={shouldDisplayPrivacyConsentCheckbox}
          />
        </div>
      );

    case DonationFormType.SINGLE_UNAUTHENTICATED_USER:
      return (
        <div className="w-full min-h-[100vh] rounded md:pb-[40px]">
          <DonationForm
            amountOptions={amountOptions}
            project={project}
            subproject={subproject}
            formType={formType}
            initialValues={initialValues}
            shouldDisplayPrivacyConsentCheckbox={shouldDisplayPrivacyConsentCheckbox}
          />
        </div>
      );

    default:
      return null;
  }
};

export default DonationDetail;
